import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Lock,
  FileText,
  Video,
  Download,
  ExternalLink,
  X,
  ChevronLeft,
  ChevronRight,
  Heart,
  CheckCircle2,
  Circle,
  Play,
  Calendar,
  Sparkles,
  Sprout,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import MarkdownContent from '../components/ui/MarkdownContent';
import ClassVideos from '../components/ClassVideos';
import ClassCalendar from '../components/ClassCalendar';

// Module colors matching Notion style
const MODULE_COLORS = [
  { bg: '#E8DFD0', text: '#8B7355' },  // Beige (Índice)
  { bg: '#C9B896', text: '#6B5B3D' },  // Tan (Glosario)
  { bg: '#D4A84B', text: '#FFFFFF' },  // Mustard/Gold (Módulo 0)
  { bg: '#E8735A', text: '#FFFFFF' },  // Coral (Módulo 1)
  { bg: '#4FB5A9', text: '#FFFFFF' },  // Teal (Módulo 2)
  { bg: '#7B8EBF', text: '#FFFFFF' },  // Blue-purple (Módulo 3)
  { bg: '#E8735A', text: '#FFFFFF' },  // Coral (Módulo 4)
  { bg: '#4FB5A9', text: '#FFFFFF' },  // Teal (Módulo 5)
  { bg: '#7B8EBF', text: '#FFFFFF' },  // Blue-purple (Módulo 6)
  { bg: '#E8735A', text: '#FFFFFF' },  // Coral (Módulo 7)
  { bg: '#4FB5A9', text: '#FFFFFF' },  // Teal (Módulo 8)
  { bg: '#7B8EBF', text: '#FFFFFF' },  // Blue-purple (Módulo 9)
  { bg: '#D4A84B', text: '#FFFFFF' },  // Mustard (Módulo 10)
  { bg: '#D4A84B', text: '#FFFFFF' },  // Mustard (Módulo 11)
  { bg: '#D4A84B', text: '#FFFFFF' },  // Mustard (Módulo 12)
];

/**
 * Turn any PDF-like URL into the pair {embedUrl, downloadUrl}.
 *
 * Handles the common Google Drive share URL variants — the user pastes the raw
 * "share" link and we transparently produce:
 *   • embedUrl: served through Drive's own JS viewer (works on mobile too)
 *   • downloadUrl: forces a direct download (Drive's uc endpoint)
 *
 * Fallback for non-Drive URLs (Cloudinary, custom CDN, etc.) is to use the
 * URL as-is for both — the native browser PDF renderer will kick in.
 */
const pdfUrls = (raw) => {
  if (!raw) return { embedUrl: '', downloadUrl: '' };
  const url = String(raw).trim();
  // Match a Google Drive file id from any of these:
  //   drive.google.com/file/d/{ID}/view?usp=...
  //   drive.google.com/open?id={ID}
  //   drive.google.com/uc?id={ID}
  //   or a bare 25-40 char id
  let driveId = null;
  const m1 = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]{20,})/);
  const m2 = url.match(/drive\.google\.com\/(?:open|uc)\?[^#]*id=([a-zA-Z0-9_-]{20,})/);
  const m3 = /^[a-zA-Z0-9_-]{25,45}$/.test(url) ? [url, url] : null;
  driveId = (m1 && m1[1]) || (m2 && m2[1]) || (m3 && m3[1]) || null;
  if (driveId) {
    return {
      embedUrl: `https://drive.google.com/file/d/${driveId}/preview`,
      downloadUrl: `https://drive.google.com/uc?export=download&id=${driveId}`,
    };
  }
  // Not Drive → use the URL directly for both
  return {
    embedUrl: `${url}${url.includes('#') ? '' : '#toolbar=1&navpanes=0'}`,
    downloadUrl: url,
  };
};

// Get module display info
const getModuleDisplay = (block, index) => {
  if (!block) return { number: String(index), title: '', phaseWord: '', colors: MODULE_COLORS[0], subtitle: '' };
  const title = block.title || '';
  // Use the color from the block if available, otherwise use default
  const defaultColors = MODULE_COLORS[index] || MODULE_COLORS[0];
  const colors = block.color 
    ? { bg: block.color, text: isLightColor(block.color) ? '#6B5B3D' : '#FFFFFF' }
    : defaultColors;
  
  // Extract module number or letter
  let displayNumber = '';
  let displayTitle = '';
  
  if (title.toLowerCase().includes('índice')) {
    displayNumber = 'i';
    displayTitle = 'Índice';
  } else if (title.toLowerCase().includes('glosario')) {
    displayNumber = 'G';
    displayTitle = 'Glosario';
  } else {
    const match = title.match(/módulo\s*(\d+)/i);
    if (match) {
      displayNumber = match[1];
      displayTitle = block.subtitle || title.replace(/módulo\s*\d+\s*[-–]?\s*/i, '').trim();
    } else {
      displayNumber = String(index);
      displayTitle = title;
    }
  }
  
  // Determine the "phase" word based on module number
  let phaseWord = '';
  const num = parseInt(displayNumber);
  if (!isNaN(num)) {
    if (num >= 1 && num <= 3) phaseWord = 'Habitar';
    else if (num >= 4 && num <= 6) phaseWord = 'Estructurar';
    else if (num >= 7 && num <= 9) phaseWord = 'Trascender';
    else if (num >= 10) phaseWord = 'Sostén energético';
  }
  
  return {
    number: displayNumber,
    title: displayTitle,
    phaseWord,
    colors,
    subtitle: block.subtitle || ''
  };
};

// Helper to determine if a color is light
const isLightColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

/* ====== Límites course — Concentric circles module navigator ====== */
const LIMITES_MODULE_META = [
  { num: 'I',   label: 'Arquitectura del límite',              detail: 'Semanas 1–2' },
  { num: 'II',  label: 'El campo relacional',                  detail: 'Semanas 3–4' },
  { num: 'III', label: 'Cuerpo, presencia y protección',       detail: 'Semanas 5–6' },
  { num: 'IV',  label: 'Amor, integración y vida cotidiana',   detail: 'Semanas 7–8' },
];

function LimitesConcentricModules({ blocks, onSelectModule, getModuleDisplay, getModuleProgress, legacyLabels = false }) {
  const [hover, setHover] = useState(null);
  // Node texts come from each module; the 1st edition keeps its original texts as fallback.
  const getMeta = (block, i) => {
    const legacy = legacyLabels ? LIMITES_MODULE_META[i] : null;
    const isPlaceholder = String(block.id).startsWith('placeholder-');
    const ownLabel = isPlaceholder ? block.title : (getModuleDisplay(block, i).title || block.title);
    return {
      num: LIMITES_MODULE_META[i]?.num || `${i + 1}`,
      label: (block.mandala_label || '').trim() || legacy?.label || ownLabel || '',
      detail: (block.mandala_detail || '').trim() || legacy?.detail || '',
    };
  };
  const cx = 400, cy = 400;
  const radii = [75, 155, 235, 320];
  const sR = 62;
  const positions = [
    { x: cx, y: cy - radii[3] },
    { x: cx + radii[3], y: cy },
    { x: cx, y: cy + radii[3] },
    { x: cx - radii[3], y: cy },
  ];

  const nodes = [];
  radii.forEach((r, ri) => {
    const count = 5 + ri * 3;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + ri * 0.4;
      nodes.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, s: 2.5 + Math.random() * 1.5 });
    }
  });

  const splitText = (t) => {
    if (t.length <= 12) return [t];
    const w = t.split(' ');
    if (w.length <= 2) return [t];
    const m = Math.ceil(w.length / 2);
    return [w.slice(0, m).join(' '), w.slice(m).join(' ')];
  };

  return (
    <>
      {/* Desktop SVG */}
      <div className="hidden md:flex justify-center py-4">
        <svg viewBox="-10 -10 820 820" className="w-full max-w-[760px]">
          <defs>
            <radialGradient id="lcCore" cx="45%" cy="42%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
              <stop offset="45%" stopColor="rgba(218,185,80,0.14)" />
              <stop offset="100%" stopColor="rgba(218,185,80,0)" />
            </radialGradient>
            <radialGradient id="lcSphere" cx="38%" cy="35%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="55%" stopColor="#f2f2f6" />
              <stop offset="100%" stopColor="#e6e6ee" />
            </radialGradient>
            <radialGradient id="lcSphereHover" cx="38%" cy="35%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="45%" stopColor="#f7f5ef" />
              <stop offset="100%" stopColor="#ebe5d6" />
            </radialGradient>
            <filter id="lcGlow"><feGaussianBlur stdDeviation="2.5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          </defs>

          {radii.map((r, i) => (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={`rgba(155,160,178,${0.22 + i * 0.07})`} strokeWidth={i === 3 ? 1.6 : 1.1} />
          ))}
          {[0, 45, 90, 135].map(a => (
            <line key={a} x1={cx + Math.cos(a * Math.PI / 180) * radii[3]} y1={cy + Math.sin(a * Math.PI / 180) * radii[3]} x2={cx - Math.cos(a * Math.PI / 180) * radii[3]} y2={cy - Math.sin(a * Math.PI / 180) * radii[3]} stroke="rgba(186,150,55,0.2)" strokeWidth="0.9" />
          ))}
          {nodes.map((n, i) => <circle key={i} cx={n.x} cy={n.y} r={n.s} fill="rgba(195,165,50,0.4)" filter="url(#lcGlow)" />)}

          {positions.map((p, i) => (
            <line key={`c${i}`} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={hover === i ? 'rgba(186,150,55,0.35)' : 'rgba(186,150,55,0.1)'} strokeWidth="0.8" strokeDasharray={hover === i ? 'none' : '5 5'} style={{ transition: 'stroke 0.4s' }} />
          ))}

          <circle cx={cx} cy={cy} r={42} fill="url(#lcCore)" stroke="rgba(186,150,55,0.32)" strokeWidth="1.3">
            <animate attributeName="r" values="42;44;42" dur="5s" repeatCount="indefinite" />
          </circle>
          <text x={cx} y={cy - 4} textAnchor="middle" fontSize="9.5" fontWeight="600" letterSpacing="2.5" fill="rgba(150,128,42,0.7)" fontFamily="serif">ESENCIA</text>
          <text x={cx} y={cy + 10} textAnchor="middle" fontSize="10.5" fill="rgba(100,100,120,0.45)" fontFamily="serif" fontStyle="italic">tu protección</text>

          {(blocks.length > 0 ? blocks.slice(0, 4) : LIMITES_MODULE_META.map((m, i) => ({ id: `placeholder-${i}`, has_access: false, title: m.label }))).map((block, i) => {
            const meta = getMeta(block, i);
            const p = positions[i];
            const isH = hover === i;
            const hasAccess = block.has_access;
            const mp = getModuleProgress(block);
            const lines = splitText(meta.label);
            return (
              <g key={block.id} transform={`translate(${p.x}, ${p.y})`}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                onClick={() => hasAccess && onSelectModule(block)}
                style={{ cursor: hasAccess ? 'pointer' : 'not-allowed' }}
              >
                {isH && <circle cx={0} cy={0} r={sR + 6} fill="none" stroke="rgba(186,150,55,0.2)" strokeWidth="1.2" />}
                <circle cx={0} cy={0} r={sR}
                  fill={isH ? 'url(#lcSphereHover)' : 'url(#lcSphere)'}
                  stroke={isH ? 'rgba(186,150,55,0.55)' : 'rgba(186,150,55,0.28)'}
                  strokeWidth={isH ? 2 : 1.4}
                  opacity={hasAccess ? 1 : 0.5}
                  style={{ transition: 'all 0.4s' }}
                />
                <text x={0} y={lines.length > 1 ? -18 : -14} textAnchor="middle" fontSize="24" fontWeight="300" letterSpacing="2"
                  fill={isH ? 'rgba(150,120,30,0.95)' : 'rgba(150,120,30,0.6)'} fontFamily="serif" style={{ transition: 'fill 0.3s' }}
                >{meta.num}</text>
                {lines.map((l, li) => (
                  <text key={li} x={0} y={(lines.length > 1 ? 6 : 10) + li * 16} textAnchor="middle" fontSize="13.5" fontWeight="700" fill="#2d3748" fontFamily="serif" style={{ letterSpacing: '0.3px' }}>{l}</text>
                ))}
                {!hasAccess && <text x={0} y={sR - 10} textAnchor="middle" fontSize="18" fill="rgba(100,100,120,0.45)">&#128274;</text>}
                {mp.total > 0 && (
                  <g>
                    <rect x={-24} y={sR - 20} width={48} height={3.5} rx={1.75} fill="rgba(0,0,0,0.06)" />
                    <rect x={-24} y={sR - 20} width={48 * mp.percent / 100} height={3.5} rx={1.75} fill={mp.percent === 100 ? '#10b981' : '#D4A84B'} />
                  </g>
                )}
                {isH && hasAccess && (
                  <g>
                    <rect x={-38} y={sR + 8} width={76} height={22} rx={11} fill="#4a5568" opacity="0.9" />
                    <text x={0} y={sR + 23} textAnchor="middle" fontSize="10" fill="white" fontFamily="serif" fontStyle="italic">Abrir módulo</text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {(blocks.length > 0 ? blocks.slice(0, 4) : LIMITES_MODULE_META.map((m, i) => ({ id: `placeholder-${i}`, has_access: false, title: m.label }))).map((block, i) => {
          const meta = getMeta(block, i);
          const hasAccess = block.has_access;
          const mp = getModuleProgress(block);
          return (
            <button key={block.id} onClick={() => hasAccess && onSelectModule(block)}
              disabled={!hasAccess}
              className={`w-full flex items-center gap-4 bg-white border rounded-2xl p-4 text-left transition-all ${hasAccess ? 'border-[rgba(186,150,55,0.25)] hover:border-[rgba(186,150,55,0.5)] hover:shadow-md' : 'border-gray-200 opacity-50'}`}
            >
              <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'radial-gradient(circle at 38% 35%, #fff, #f0f0f4, #e4e4ec)', border: '1.5px solid rgba(186,150,55,0.28)' }}>
                <span className="text-lg font-heading" style={{ color: 'rgba(150,120,30,0.7)', letterSpacing: '2px' }}>{meta.num}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-heading text-[#4a5568] font-semibold">{meta.label}</p>
                {meta.detail && <p className="text-xs text-[#a0aec0] italic mt-0.5">{meta.detail}</p>}
                {mp.total > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${mp.percent}%`, backgroundColor: mp.percent === 100 ? '#10b981' : '#D4A84B' }} />
                    </div>
                    <span className="text-[10px] text-gray-400">{mp.completed}/{mp.total}</span>
                  </div>
                )}
              </div>
              {!hasAccess && <Lock className="w-4 h-4 text-gray-300 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ====== SpheresSection — round-image gallery for preliminary/practice spheres ====== */
function SpheresSection({ sectionTitle, blocks, variant, onSelect, testId, testPrefix }) {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="mb-10" data-testid={testId}>
      <h2 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-5">{sectionTitle}</h2>
      <div className="grid grid-cols-3 gap-4 sm:gap-8 w-full">
        {blocks.map((b, idx) => {
          const isFavorites = !!b.is_favorites_block;
          // `has_access` is computed by the backend. Missing/undefined means open
          // (backward-compat for courses without access lists yet).
          const hasAccess = b.has_access !== false;
          const isLocked = !hasAccess;
          const handleClick = () => {
            if (isLocked) {
              toast.info('Esta esfera aún no está disponible. Pronto se abrirá.');
              return;
            }
            onSelect(b);
          };
          return (
            <button
              key={b.id}
              type="button"
              onClick={handleClick}
              className={`group flex flex-col items-center text-center focus:outline-none ${isLocked ? 'cursor-not-allowed' : ''}`}
              data-testid={`${testPrefix}-${idx}`}
              aria-disabled={isLocked}
            >
              <div className={`relative aspect-square w-full max-w-[220px] mx-auto rounded-full overflow-hidden bg-[#FBF8F1] border-2 transition-all duration-300 ${
                isLocked
                  ? 'border-gray-200 opacity-60 grayscale'
                  : isFavorites
                    ? 'border-[#D4A84B]/60 group-hover:border-[#D4A84B] group-hover:shadow-lg group-hover:shadow-[#D4A84B]/25'
                    : 'border-[#D4A84B]/30 group-hover:border-[#D4A84B]/70 group-hover:shadow-lg group-hover:shadow-[#D4A84B]/15'
              }`}>
                {b.image_url ? (
                  <img
                    src={b.image_url}
                    alt={b.title}
                    className={`w-full h-full object-cover transition-transform duration-500 ${isLocked ? '' : 'group-hover:scale-105'}`}
                    style={{ objectPosition: b.image_position || 'center center' }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#D4A84B]/50">
                    {isFavorites ? <Sprout className="w-12 h-12" /> : <FileText className="w-10 h-10" />}
                  </div>
                )}
                {isLocked && (
                  <div className="absolute inset-0 bg-black/35 flex items-center justify-center" data-testid={`${testPrefix}-lock-${idx}`}>
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-md">
                      <Lock className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                )}
                {isFavorites && !isLocked && (
                  <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm border border-[#D4A84B]/40 flex items-center justify-center shadow-sm">
                    <Sprout className="w-4 h-4 text-[#D4A84B]" />
                  </div>
                )}
              </div>
              <p className={`mt-3 sm:mt-4 text-sm sm:text-base font-heading transition-colors ${
                isLocked
                  ? 'text-gray-400'
                  : 'text-[hsl(var(--text-primary))] group-hover:text-[#D4A84B]'
              }`}>
                {b.title}
              </p>
              {isLocked && (
                <span className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">Próximamente</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ====== SelectableText — wraps content & emits onSave when user highlights text ====== */
function SelectableText({ sourceMeta, onRequestSave, children }) {
  const ref = useRef(null);

  const handleEnd = useCallback(() => {
    // Defer slightly so the selection is fully committed (esp. on mobile touch)
    setTimeout(() => {
      const sel = window.getSelection ? window.getSelection() : null;
      if (!sel || sel.isCollapsed) return;
      const text = (sel.toString() || '').trim();
      if (text.length < 3) return;
      // Confirm selection lives inside this wrapper
      if (!ref.current) return;
      let inside = false;
      try {
        for (let i = 0; i < sel.rangeCount; i++) {
          const r = sel.getRangeAt(i);
          if (ref.current.contains(r.commonAncestorContainer)) { inside = true; break; }
        }
      } catch (_) { /* noop */ }
      if (!inside) return;
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) return;
      onRequestSave({
        text,
        rect: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
        meta: sourceMeta,
      });
    }, 10);
  }, [onRequestSave, sourceMeta]);

  return (
    <div ref={ref} onMouseUp={handleEnd} onTouchEnd={handleEnd} className="seed-selectable">
      {children}
    </div>
  );
}

/* ====== FavoritesMasonry — renders user's saved phrases in a masonry-like grid ====== */
function FavoritesMasonry({ favorites, onDelete, introText }) {
  if (!favorites || favorites.length === 0) {
    return (
      <div data-testid="favorites-empty" className="text-center py-16 bg-[#FBF8F1]/60 rounded-2xl border border-[#D4A84B]/15">
        <div className="w-14 h-14 mx-auto rounded-full bg-white border border-[#D4A84B]/30 flex items-center justify-center mb-4">
          <Sprout className="w-6 h-6 text-[#D4A84B]" />
        </div>
        <p className="text-gray-700 font-heading text-lg mb-2">Tu herbario está vacío</p>
        <p className="text-gray-500 text-sm max-w-md mx-auto px-6">
          Mientras leas cualquier contenido del curso, selecciona una frase que te resuene y guárdala aquí. Será tu colección íntima de semillas de luz.
        </p>
      </div>
    );
  }
  return (
    <>
      {introText && introText.trim() && (
        <div className="prose max-w-none mb-8 text-gray-600">
          <MarkdownContent content={introText} />
        </div>
      )}
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="favorites-masonry">
        {favorites.map((f) => (
          <div
            key={f.id}
            className="mb-5 break-inside-avoid group relative bg-gradient-to-br from-[#FDFBF5] to-white border border-[#D4A84B]/20 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
            data-testid={`favorite-card-${f.id}`}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#D4A84B]/70 mb-3" />
            <p className="text-[15px] text-gray-700 leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
              "{f.text}"
            </p>
            <div className="mt-4 pt-3 border-t border-[#D4A84B]/15 flex items-center justify-between gap-2">
              <div className="min-w-0">
                {f.parent_title && (
                  <p className="text-[10px] uppercase tracking-wider text-[#8a7428]/70 truncate">{f.parent_title}</p>
                )}
                {f.source_title && (
                  <p className="text-xs text-gray-500 truncate">{f.source_title}</p>
                )}
              </div>
              <button
                onClick={() => onDelete(f.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50"
                title="Retirar semilla"
                data-testid={`favorite-delete-${f.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const FacilitadorasCurso = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const courseSlug = slug || 'facilitadoras-sosten-energetico';
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [togglingProgress, setTogglingProgress] = useState(false);
  const [showClassVideos, setShowClassVideos] = useState(false);
  const [videoCount, setVideoCount] = useState(0);
  const [showClassCalendar, setShowClassCalendar] = useState(false);
  const [calendarData, setCalendarData] = useState({ count: 0, enabled: false });
  const [showFavoritesView, setShowFavoritesView] = useState(false);
  // Esferas introductorias (preliminary modules) — only Límites uses this for now.
  const [selectedPreliminary, setSelectedPreliminary] = useState(null);
  const [selectedPreliminarySubPage, setSelectedPreliminarySubPage] = useState(null);

  // "Tus semillas de luz" — user-saved highlighted phrases for this course
  const [favorites, setFavorites] = useState([]);
  const [selectionTooltip, setSelectionTooltip] = useState(null); // { x, y, text, meta }
  const [savingFavorite, setSavingFavorite] = useState(false);

  // Fetch saved favorites once the course content is loaded
  useEffect(() => {
    let cancelled = false;
    api.get(`/special-courses/${courseSlug}/favorites`)
      .then(res => { if (!cancelled) setFavorites(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setFavorites([]); });
    return () => { cancelled = true; };
  }, [courseSlug]);

  // Hide tooltip when user clicks outside / scrolls / selection cleared
  useEffect(() => {
    if (!selectionTooltip) return;
    const onDown = (e) => {
      // If clicking inside the tooltip itself, let its handler run
      if (e.target && e.target.closest && e.target.closest('[data-testid="seed-save-tooltip"]')) return;
      setSelectionTooltip(null);
    };
    const onScroll = () => setSelectionTooltip(null);
    const onSelChange = () => {
      const sel = window.getSelection ? window.getSelection() : null;
      if (!sel || sel.isCollapsed || (sel.toString() || '').trim().length < 3) {
        setSelectionTooltip(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('selectionchange', onSelChange);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('selectionchange', onSelChange);
    };
  }, [selectionTooltip]);

  const requestSaveFavorite = useCallback(({ text, rect, meta }) => {
    // Position the tooltip above the selection (or below if too close to top)
    const tooltipHeight = 44;
    const padding = 10;
    const y = rect.top > tooltipHeight + padding ? rect.top - tooltipHeight - 6 : rect.bottom + 6;
    const x = Math.min(Math.max(rect.left + rect.width / 2, 110), window.innerWidth - 110);
    setSelectionTooltip({ x, y, text, meta });
  }, []);

  const handleSaveFavorite = async () => {
    if (!selectionTooltip || savingFavorite) return;
    const { text, meta } = selectionTooltip;
    setSavingFavorite(true);
    try {
      const res = await api.post(`/special-courses/${courseSlug}/favorites`, {
        text,
        source_kind: meta?.kind || 'unknown',
        source_id: meta?.id || null,
        source_title: meta?.title || '',
        parent_title: meta?.parent_title || '',
        parent_id: meta?.parent_id || null,
      });
      setFavorites(prev => [res.data, ...prev]);
      toast.success('Guardado en tus semillas de luz');
      // Clear browser selection so the tooltip vanishes
      try { window.getSelection().removeAllRanges(); } catch (_) { /* noop */ }
      setSelectionTooltip(null);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar la semilla');
    } finally {
      setSavingFavorite(false);
    }
  };

  const handleDeleteFavorite = async (favoriteId) => {
    try {
      await api.delete(`/special-courses/${courseSlug}/favorites/${favoriteId}`);
      setFavorites(prev => prev.filter(f => f.id !== favoriteId));
      toast.success('Semilla retirada');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo eliminar la semilla');
    }
  };

  const renderSeedTooltip = () => {
    if (!selectionTooltip) return null;
    return (
      <div
        data-testid="seed-save-tooltip"
        className="fixed z-[80] -translate-x-1/2 bg-[#2d3748] text-white rounded-full shadow-xl border border-[#D4A84B]/40 px-4 py-2 flex items-center gap-2 text-sm animate-in fade-in slide-in-from-bottom-1 duration-200"
        style={{ top: selectionTooltip.y, left: selectionTooltip.x }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <button
          type="button"
          onClick={handleSaveFavorite}
          disabled={savingFavorite}
          className="inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="seed-save-btn"
        >
          <Sprout className="w-4 h-4 text-[#D4A84B]" />
          <span>{savingFavorite ? 'Guardando…' : 'Guardar en tus semillas de luz'}</span>
        </button>
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/special-courses/${courseSlug}/content`);
        setData(response.data);
        setCompletedLessons(response.data?.enrollment?.completed_lessons || []);
      } catch (error) {
        if (error.response?.status === 403) {
          const errorMsg = error.response?.data?.detail || 'No tienes acceso a este curso';
          if (errorMsg.includes('no ha comenzado') || errorMsg.includes('Próximamente')) {
            toast.info(errorMsg);
            navigate('/mi-camino');
          } else {
            toast.error(errorMsg);
            navigate(`/formaciones/${courseSlug}`);
          }
        } else {
          console.error('Error loading course:', error);
          setData(null);
        }
      } finally {
        setLoading(false);
      }
    };
    // Fetch video count and calendar info in parallel
    api.get(`/special-courses/${courseSlug}/class-videos`)
      .then(res => setVideoCount(res.data?.total || 0))
      .catch(() => {});
    api.get(`/special-courses/${courseSlug}/class-sessions`)
      .then(res => setCalendarData({ count: res.data?.total || 0, enabled: res.data?.calendar_enabled !== false }))
      .catch(() => {});
    setSelectedModule(null);
    setSelectedLesson(null);
    fetchData();
  }, [navigate, courseSlug]);

  const toggleLessonComplete = async (lessonId) => {
    setTogglingProgress(true);
    try {
      const isCompleted = completedLessons.includes(lessonId);
      const endpoint = isCompleted ? 'uncomplete' : 'complete';
      const response = await api.post(`/special-courses/${courseSlug}/progress/${endpoint}`, { lesson_id: lessonId });
      setCompletedLessons(response.data.completed_lessons || []);
      toast.success(isCompleted ? 'Lección desmarcada' : 'Lección completada');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar progreso');
    } finally {
      setTogglingProgress(false);
    }
  };

  // Calculate progress helpers
  const getModuleProgress = (block) => {
    const subs = block.sub_blocks || [];
    if (subs.length === 0) return { completed: 0, total: 0, percent: 0 };
    const completed = subs.filter(s => completedLessons.includes(s.id)).length;
    return { completed, total: subs.length, percent: Math.round((completed / subs.length) * 100) };
  };

  const getOverallProgress = (blocks) => {
    const allLessons = blocks.flatMap(b => b.sub_blocks || []);
    // Also count preliminary spheres' sub-pages (esferas introductorias) so the
    // global progress bar reflects the whole learning path, not just the program.
    const allSubPages = (preliminaryBlocks || []).flatMap(s => s.sub_pages || []);
    const total = allLessons.length + allSubPages.length;
    if (total === 0) return { completed: 0, total: 0, percent: 0 };
    const completed = [...allLessons, ...allSubPages].filter(s => completedLessons.includes(s.id)).length;
    return { completed, total, percent: Math.round((completed / total) * 100) };
  };

  // Parse markdown-like content for rendering
  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#D4A84B] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando tu espacio...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <ExternalLink className="w-7 h-7 text-gray-400" />
          </div>
          <h2 className="text-xl font-heading text-gray-700 mb-2">
            No se pudo cargar el contenido
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Puede ser un problema temporal. Intenta recargar la página.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate('/mi-camino')}
              variant="outline"
              className="rounded-xl"
            >
              Volver a Mi Camino
            </Button>
            <Button
              onClick={() => window.location.reload()}
              className="bg-[#D4A84B] hover:bg-[#c49b3f] text-white rounded-xl"
            >
              Recargar página
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { course: rawCourse, blocks: courseBlocks, preliminary_blocks: rawPreliminary, practice_blocks: rawPractice, section_titles: sectionTitles, enrollment } = data;
  const course = rawCourse || {};
  const blocks = courseBlocks || [];
  const preliminaryBlocks = [...(rawPreliminary || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const practiceBlocks = [...(rawPractice || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const preliminaryTitle = sectionTitles?.preliminary_blocks_section_title || 'Esferas introductorias';
  const practiceTitle = sectionTitles?.practice_blocks_section_title || 'Esferas prácticas';
  const isLimitesTpl = courseSlug === 'limites' || course.student_template === 'limites';

  // Show class videos section
  if (showClassVideos) {
    return (
      <div className="pt-20 min-h-screen bg-[#FDFBF7]">
        <ClassVideos
          courseSlug={courseSlug}
          onBack={() => setShowClassVideos(false)}
        />
      </div>
    );
  }

  // Show class calendar section
  if (showClassCalendar) {
    return (
      <div className="pt-20 min-h-screen bg-[#FDFBF7]">
        <ClassCalendar
          courseSlug={courseSlug}
          onBack={() => setShowClassCalendar(false)}
        />
      </div>
    );
  }

  // Show "Semillas de Luz" view — user's saved highlighted phrases for this course
  if (showFavoritesView) {
    return (
      <div className="pt-20 min-h-screen bg-white pb-24">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={() => { setShowFavoritesView(false); window.scrollTo(0, 0); }}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
            data-testid="favorites-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver al curso
          </button>
          <div className="flex items-center gap-5 mb-8">
            <div className="w-20 h-20 rounded-full bg-[#FBF8F1] border-2 border-[#D4A84B]/40 flex items-center justify-center flex-shrink-0">
              <Sprout className="w-8 h-8 text-[#D4A84B]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#D4A84B]/80 mb-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Tu herbario íntimo
              </p>
              <h1 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">
                Semillas de Luz
              </h1>
            </div>
          </div>
          <FavoritesMasonry
            favorites={favorites}
            onDelete={handleDeleteFavorite}
            introText="Aquí brilla todo lo que has subrayado a lo largo del curso. Cada frase es una semilla que sembraste en ti misma."
          />
        </div>
        {renderSeedTooltip()}
      </div>
    );
  }

  // If a lesson is selected, show lesson detail
  if (selectedLesson && selectedModule) {
    const subBlocks = selectedModule.sub_blocks || [];
    const currentLessonIndex = subBlocks.findIndex(s => s.id === selectedLesson.id);
    const prevLesson = currentLessonIndex > 0 ? subBlocks[currentLessonIndex - 1] : null;
    const nextLesson = currentLessonIndex < subBlocks.length - 1 ? subBlocks[currentLessonIndex + 1] : null;
    
    // Cross-module navigation
    const currentModuleIndex = blocks.findIndex(b => b.id === selectedModule.id);
    const prevModule = currentModuleIndex > 0 ? blocks[currentModuleIndex - 1] : null;
    const nextModule = currentModuleIndex < blocks.length - 1 ? blocks[currentModuleIndex + 1] : null;
    
    const goToLesson = (lesson) => { setSelectedLesson(lesson); window.scrollTo(0, 0); };
    const goToModule = (mod) => { setSelectedModule(mod); setSelectedLesson(null); window.scrollTo(0, 0); };

    return (
      <div className="pt-20 min-h-screen bg-white pb-24">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Back button */}
          <button
            onClick={() => { setSelectedLesson(null); window.scrollTo(0, 0); }}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver a {selectedModule.title || 'módulo'}
          </button>

          {/* Lesson content */}
          <div className="bg-white rounded-xl">
            {selectedLesson.image_url && (
              <img 
                src={selectedLesson.image_url}
                alt={selectedLesson.title}
                className="w-full h-64 object-cover rounded-xl mb-6"
                style={{ objectPosition: selectedLesson.image_position || 'center center' }}
              />
            )}
            <h1 className="text-3xl font-bold text-gray-800 mb-4">{selectedLesson.title}</h1>
            {selectedLesson.subtitle && (
              <p className="text-gray-500 mb-6">{selectedLesson.subtitle}</p>
            )}
            
            <div className="prose max-w-none">
              {/* PDF — embebido para lectura in-situ + descarga secundaria */}
              {selectedLesson.content_type === 'pdf' && selectedLesson.content && (() => {
                const { embedUrl, downloadUrl } = pdfUrls(selectedLesson.content);
                return (
                <div className="mb-6" data-testid="pdf-viewer-wrap">
                  {/* Mobile-first: show download pill ABOVE the viewer because many mobile browsers
                      render inline PDFs as a blank box. Keeps the fallback within the fold. */}
                  <div className="sm:hidden mb-3 flex justify-end">
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="inline-flex items-center gap-1.5 text-xs bg-[#D4A84B] text-[#1a140a] px-4 py-2 rounded-full font-medium"
                      data-testid="pdf-download-btn-mobile"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Abrir / Descargar PDF
                    </a>
                  </div>
                  <div className="relative bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden">
                    <iframe
                      src={embedUrl}
                      title={selectedLesson.title || 'PDF'}
                      className="w-full h-[70vh] sm:min-h-[500px] bg-white"
                      loading="lazy"
                      allow="autoplay"
                      data-testid="pdf-embed-iframe"
                    />
                    <noscript>
                      <a href={downloadUrl} target="_blank" rel="noopener noreferrer">Abrir PDF</a>
                    </noscript>
                  </div>
                  <div className="hidden sm:flex items-center justify-between gap-3 mt-3 flex-wrap">
                    <p className="text-xs text-gray-500 italic">
                      Léelo aquí con calma. Si prefieres, también puedes descargarlo.
                    </p>
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#D4A84B] px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#D4A84B]/40 transition-colors"
                      data-testid="pdf-download-btn"
                    >
                      <Download className="w-3 h-3" />
                      Descargar PDF
                    </a>
                  </div>
                  <p className="text-[11px] text-gray-400 italic mt-2 leading-relaxed">
                    Si el visor no carga, prueba a descargar el PDF con el botón — a veces algunos navegadores móviles no lo muestran embebido.
                  </p>
                </div>
                );
              })()}
              {/* Link */}
              {selectedLesson.content_type === 'link' && selectedLesson.content && (
                <a 
                  href={selectedLesson.content}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors mb-4"
                >
                  <ExternalLink className="w-5 h-5" />
                  Abrir enlace
                </a>
              )}
              {/* Rich content: text + embedded videos via MarkdownContent */}
              {selectedLesson.content && (selectedLesson.content_type === 'text' || selectedLesson.content_type === 'video') && (
                <div className="text-gray-700">
                  <SelectableText
                    sourceMeta={{
                      kind: 'lesson',
                      id: selectedLesson.id,
                      title: selectedLesson.title || '',
                      parent_id: selectedModule.id,
                      parent_title: selectedModule.title || '',
                    }}
                    onRequestSave={requestSaveFavorite}
                  >
                    <MarkdownContent content={selectedLesson.content} />
                  </SelectableText>
                </div>
              )}
              {!selectedLesson.content && (
                <p className="text-gray-400 italic">Contenido en preparación...</p>
              )}
            </div>

            {/* Mark as completed button */}
            <div className="mt-10 pt-6 border-t border-gray-100">
              <button
                data-testid="toggle-lesson-complete"
                onClick={() => toggleLessonComplete(selectedLesson.id)}
                disabled={togglingProgress}
                className={`inline-flex items-center gap-3 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                  completedLessons.includes(selectedLesson.id)
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                    : 'bg-[#D4A84B]/10 text-[#D4A84B] border border-[#D4A84B]/30 hover:bg-[#D4A84B]/20'
                }`}
              >
                {completedLessons.includes(selectedLesson.id) ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    Lección completada
                  </>
                ) : (
                  <>
                    <Circle className="w-5 h-5" />
                    Marcar como completada
                  </>
                )}
              </button>
            </div>

            {/* Prev/Next lesson navigation */}
            <div className="mt-8 pt-6 border-t border-gray-100 flex items-stretch gap-4" data-testid="lesson-nav">
              <div className="flex-1">
                {prevLesson ? (
                  <button
                    onClick={() => goToLesson(prevLesson)}
                    className="w-full text-left p-4 rounded-xl border border-gray-100 hover:border-[#D4A84B]/30 hover:bg-[#D4A84B]/5 transition-all group"
                    data-testid="prev-lesson-btn"
                  >
                    <span className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      <ChevronLeft className="w-3 h-3" /> Anterior
                    </span>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#D4A84B] line-clamp-1">
                      {prevLesson.title}
                    </span>
                  </button>
                ) : prevModule ? (
                  <button
                    onClick={() => goToModule(prevModule)}
                    className="w-full text-left p-4 rounded-xl border border-gray-100 hover:border-[#D4A84B]/30 hover:bg-[#D4A84B]/5 transition-all group"
                    data-testid="prev-module-btn"
                  >
                    <span className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      <ChevronLeft className="w-3 h-3" /> Módulo anterior
                    </span>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#D4A84B] line-clamp-1">
                      {prevModule.title}
                    </span>
                  </button>
                ) : <div />}
              </div>
              <div className="flex-1">
                {nextLesson ? (
                  <button
                    onClick={() => goToLesson(nextLesson)}
                    className="w-full text-right p-4 rounded-xl border border-gray-100 hover:border-[#D4A84B]/30 hover:bg-[#D4A84B]/5 transition-all group"
                    data-testid="next-lesson-btn"
                  >
                    <span className="flex items-center justify-end gap-1 text-xs text-gray-400 mb-1">
                      Siguiente <ChevronRight className="w-3 h-3" />
                    </span>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#D4A84B] line-clamp-1">
                      {nextLesson.title}
                    </span>
                  </button>
                ) : nextModule ? (
                  <button
                    onClick={() => goToModule(nextModule)}
                    className="w-full text-right p-4 rounded-xl border border-gray-100 hover:border-[#D4A84B]/30 hover:bg-[#D4A84B]/5 transition-all group"
                    data-testid="next-module-btn"
                  >
                    <span className="flex items-center justify-end gap-1 text-xs text-gray-400 mb-1">
                      Módulo siguiente <ChevronRight className="w-3 h-3" />
                    </span>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#D4A84B] line-clamp-1">
                      {nextModule.title}
                    </span>
                  </button>
                ) : <div />}
              </div>
            </div>
          </div>
        </div>
        {renderSeedTooltip()}
      </div>
    );
  }

  // If a module is selected, show lessons gallery
  if (selectedModule) {
    const hasAccess = selectedModule.has_access;
    const moduleDisplay = getModuleDisplay(selectedModule, blocks.findIndex(b => b.id === selectedModule.id));
    const subBlocks = selectedModule.sub_blocks || [];

    return (
      <div className="pt-20 min-h-screen bg-white pb-24">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Back button */}
          <button
            onClick={() => { setSelectedModule(null); window.scrollTo(0, 0); }}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver al programa
          </button>

          {/* Module header */}
          <div className="mb-8">
            {isLimitesTpl ? (
              <>
                <h1 className="text-3xl font-heading text-gray-800 mb-2" data-testid="formative-sphere-title">
                  {selectedModule.title || 'Esfera formativa'}
                </h1>
                {selectedModule.subtitle && (
                  <p className="text-gray-500 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    {selectedModule.subtitle}
                  </p>
                )}
              </>
            ) : (
              <>
                <div 
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm mb-4"
                  style={{ backgroundColor: moduleDisplay.colors.bg + '30', color: moduleDisplay.colors.bg }}
                >
                  <span>Sostén energético</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {moduleDisplay.phaseWord && <span>{moduleDisplay.phaseWord} - </span>}
                  Módulo {moduleDisplay.number}
                </h1>
                <p className="text-gray-500 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  {moduleDisplay.title || moduleDisplay.subtitle}
                </p>
              </>
            )}
            {/* Module progress */}
            {(() => {
              const mp = getModuleProgress(selectedModule);
              return mp.total > 0 ? (
                <div className="mt-4 flex items-center gap-3" data-testid="module-progress">
                  <div className="flex-1 max-w-xs h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${mp.percent}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500">{mp.completed}/{mp.total} completadas</span>
                </div>
              ) : null;
            })()}
          </div>

          {/* Lessons gallery */}
          {(() => {
            if (subBlocks.length === 0) {
              return (
                <div className="text-center py-16 bg-gray-50 rounded-xl">
                  <p className="text-gray-500">Este módulo está siendo preparado.</p>
                  <p className="text-gray-400 text-sm mt-2">Pronto tendrás contenido disponible.</p>
                </div>
              );
            }

            const sortedAll = [...subBlocks].sort((a, b) => (a.order || 0) - (b.order || 0));
            const renderCard = (lesson, index) => (
              <button
                key={lesson.id}
                onClick={() => { setSelectedLesson(lesson); window.scrollTo(0, 0); }}
                className="group text-left bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
                data-testid={`lesson-${lesson.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  {lesson.image_url ? (
                    <img 
                      src={lesson.image_url}
                      alt={lesson.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ objectPosition: lesson.image_position || 'center center' }}
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: moduleDisplay.colors.bg + '40' }}
                    >
                      <FileText className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start gap-2">
                    {completedLessons.includes(lesson.id) ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                    )}
                    <div>
                      <p className={`font-medium group-hover:text-[#D4A84B] transition-colors ${
                        completedLessons.includes(lesson.id) ? 'text-emerald-700' : 'text-gray-800'
                      }`}>
                        {index + 1}. {lesson.title}
                      </p>
                      {lesson.subtitle && (
                        <p className="text-sm text-gray-500 mt-1">{lesson.subtitle}</p>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );

            // Límites: render 2 stacked sections (Módulo 1 / Módulo 2) with editable titles
            if (isLimitesTpl) {
              const m1Title = selectedModule.module_1_title || 'Módulo 1';
              const m2Title = selectedModule.module_2_title || 'Módulo 2';
              const group1 = sortedAll.filter(s => (Number(s.module_index) === 2 ? 2 : 1) === 1);
              const group2 = sortedAll.filter(s => (Number(s.module_index) === 2 ? 2 : 1) === 2);
              const renderGroup = (title, items, idx) => (
                <div className="mb-12" data-testid={`module-section-${idx}`}>
                  <div className="flex items-baseline gap-3 mb-5 pb-3 border-b border-[#D4A84B]/20">
                    <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))]">{title}</h2>
                  </div>
                  {items.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {items.map((lesson, i) => renderCard(lesson, i))}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-50/60 rounded-xl">
                      <p className="text-gray-400 text-sm italic">Pronto tendrás contenido en esta sección.</p>
                    </div>
                  )}
                </div>
              );
              return (
                <>
                  {renderGroup(m1Title, group1, 1)}
                  {renderGroup(m2Title, group2, 2)}
                </>
              );
            }

            // Default (Facilitadoras, etc.): single grid like before
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedAll.map((lesson, index) => renderCard(lesson, index))}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Selected preliminary module ("esfera introductoria") detail view
  if (selectedPreliminary) {
    const subPages = [...(selectedPreliminary.sub_pages || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const hasSubPages = subPages.length > 0;
    // Current page being shown: either an explicit sub-page or the parent "single content"
    const currentPage = selectedPreliminarySubPage || (hasSubPages ? null : selectedPreliminary);

    const renderContent = (page, sourceMeta) => {
      const ctype = page.content_type || 'text';
      const ctext = page.content_text || '';
      const curl = page.content_url || '';
      if (ctype === 'video' && curl) {
        const ytMatch = curl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{6,})/);
        const driveMatch = curl.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?id=)([a-zA-Z0-9_-]{20,})/);
        let embedSrc = null;
        if (ytMatch) embedSrc = `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?rel=0&modestbranding=1`;
        else if (driveMatch) embedSrc = `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
        if (embedSrc) {
          return (
            <div className="relative w-full bg-black rounded-2xl overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={embedSrc}
                title={page.title}
                className="absolute inset-0 w-full h-full"
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
      }
      if (ctype === 'pdf' && curl) {
        const { embedUrl, downloadUrl } = pdfUrls(curl);
        return (
          <div className="mb-6" data-testid="pdf-viewer-wrap-sub">
            <div className="sm:hidden mb-3 flex justify-end">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-1.5 text-xs bg-[#D4A84B] text-[#1a140a] px-4 py-2 rounded-full font-medium"
                data-testid="pdf-download-btn-mobile-sub"
              >
                <Download className="w-3.5 h-3.5" />
                Abrir / Descargar PDF
              </a>
            </div>
            <div className="relative bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden">
              <iframe
                src={embedUrl}
                title="PDF"
                className="w-full h-[70vh] sm:min-h-[500px] bg-white"
                loading="lazy"
                allow="autoplay"
                data-testid="pdf-embed-iframe-sub"
              />
            </div>
            <div className="hidden sm:flex items-center justify-between gap-3 mt-3 flex-wrap">
              <p className="text-xs text-gray-500 italic">
                Léelo aquí con calma. Si prefieres, también puedes descargarlo.
              </p>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#D4A84B] px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#D4A84B]/40 transition-colors"
                data-testid="pdf-download-btn-sub"
              >
                <Download className="w-3 h-3" />
                Descargar PDF
              </a>
            </div>
          </div>
        );
      }
      if (ctext && ctext.trim()) {
        const inner = (
          <div className="prose prose-lg max-w-none">
            <MarkdownContent content={ctext} />
          </div>
        );
        return sourceMeta ? (
          <SelectableText sourceMeta={sourceMeta} onRequestSave={requestSaveFavorite}>
            {inner}
          </SelectableText>
        ) : inner;
      }
      return (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <p className="text-gray-500">Este apartado está siendo preparado.</p>
          <p className="text-gray-400 text-sm mt-2">Pronto tendrás contenido disponible.</p>
        </div>
      );
    };

    // Viewing a specific sub-page → show single page detail + back-to-index
    if (selectedPreliminarySubPage) {
      // Build a global flat list of pages (sphere1.sub_pages + sphere2.sub_pages + ...) so
      // that "Anterior/Siguiente" can hop across spheres just like in the formal program.
      const flatPages = [];
      preliminaryBlocks.forEach(sphere => {
        const subs = [...(sphere.sub_pages || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
        subs.forEach(p => flatPages.push({ ...p, _sphere: sphere }));
      });
      const currentIdx = flatPages.findIndex(p => p.id === selectedPreliminarySubPage.id);
      const prevPage = currentIdx > 0 ? flatPages[currentIdx - 1] : null;
      const nextPage = currentIdx < flatPages.length - 1 ? flatPages[currentIdx + 1] : null;
      const goToSubPage = (p) => {
        if (p._sphere && p._sphere.id !== selectedPreliminary.id) {
          setSelectedPreliminary(p._sphere);
        }
        setSelectedPreliminarySubPage(p);
        window.scrollTo(0, 0);
      };
      const isCompleted = completedLessons.includes(selectedPreliminarySubPage.id);

      return (
        <div className="pt-20 min-h-screen bg-white pb-24">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <button
              onClick={() => { setSelectedPreliminarySubPage(null); window.scrollTo(0, 0); }}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
              data-testid="subpage-back-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver al índice de {selectedPreliminary.title}
            </button>
            <div className="flex items-center gap-5 mb-8">
              <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#FBF8F1] border-2 border-[#D4A84B]/40 flex-shrink-0">
                {selectedPreliminarySubPage.image_url ? (
                  <img src={selectedPreliminarySubPage.image_url} alt={selectedPreliminarySubPage.title} className="w-full h-full object-cover" style={{ objectPosition: selectedPreliminarySubPage.image_position || 'center center' }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#D4A84B]/60"><FileText className="w-7 h-7" /></div>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#D4A84B]/80 mb-1">{selectedPreliminary.title}</p>
                <h1 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">{selectedPreliminarySubPage.title}</h1>
              </div>
            </div>
            {renderContent(selectedPreliminarySubPage, {
              kind: 'preliminary_subpage',
              id: selectedPreliminarySubPage.id,
              title: selectedPreliminarySubPage.title || '',
              parent_id: selectedPreliminary.id,
              parent_title: selectedPreliminary.title || '',
            })}

            {/* Mark as completed button */}
            <div className="mt-10 pt-6 border-t border-gray-100">
              <button
                data-testid="toggle-subpage-complete"
                onClick={() => toggleLessonComplete(selectedPreliminarySubPage.id)}
                disabled={togglingProgress}
                className={`inline-flex items-center gap-3 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                  isCompleted
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                    : 'bg-[#D4A84B]/10 text-[#D4A84B] border border-[#D4A84B]/30 hover:bg-[#D4A84B]/20'
                }`}
              >
                {isCompleted ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    Lectura consciente completada
                  </>
                ) : (
                  <>
                    <Circle className="w-5 h-5" />
                    Marcar como completada
                  </>
                )}
              </button>
            </div>

            {/* Prev/Next sub-page navigation (across all spheres) */}
            <div className="mt-8 pt-6 border-t border-gray-100 flex items-stretch gap-4" data-testid="subpage-nav">
              <div className="flex-1">
                {prevPage ? (
                  <button
                    onClick={() => goToSubPage(prevPage)}
                    className="w-full text-left p-4 rounded-xl border border-gray-100 hover:border-[#D4A84B]/30 hover:bg-[#D4A84B]/5 transition-all group"
                    data-testid="prev-subpage-btn"
                  >
                    <span className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      <ChevronLeft className="w-3 h-3" /> {prevPage._sphere?.id !== selectedPreliminary.id ? `Esfera anterior · ${prevPage._sphere?.title}` : 'Anterior'}
                    </span>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#D4A84B] line-clamp-1">{prevPage.title}</span>
                  </button>
                ) : <div />}
              </div>
              <div className="flex-1">
                {nextPage ? (
                  <button
                    onClick={() => goToSubPage(nextPage)}
                    className="w-full text-right p-4 rounded-xl border border-gray-100 hover:border-[#D4A84B]/30 hover:bg-[#D4A84B]/5 transition-all group"
                    data-testid="next-subpage-btn"
                  >
                    <span className="flex items-center justify-end gap-1 text-xs text-gray-400 mb-1">
                      {nextPage._sphere?.id !== selectedPreliminary.id ? `Esfera siguiente · ${nextPage._sphere?.title}` : 'Siguiente'} <ChevronRight className="w-3 h-3" />
                    </span>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#D4A84B] line-clamp-1">{nextPage.title}</span>
                  </button>
                ) : <div />}
              </div>
            </div>
          </div>
          {renderSeedTooltip()}
        </div>
      );
    }

    // Otherwise: sphere detail view = favorites masonry OR list of sub-pages (numbered cards) OR single content
    const isFavoritesSphere = !!selectedPreliminary.is_favorites_block;
    return (
      <div className="pt-20 min-h-screen bg-white pb-24">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={() => { setSelectedPreliminary(null); window.scrollTo(0, 0); }}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
            data-testid="preliminary-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver al curso
          </button>
          <div className="flex items-center gap-5 mb-8">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[#FBF8F1] border-2 border-[#D4A84B]/40 flex-shrink-0">
              {selectedPreliminary.image_url ? (
                <img
                  src={selectedPreliminary.image_url}
                  alt={selectedPreliminary.title}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: selectedPreliminary.image_position || 'center center' }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#D4A84B]/60">
                  {isFavoritesSphere ? <Sprout className="w-7 h-7" /> : <FileText className="w-7 h-7" />}
                </div>
              )}
            </div>
            <div>
              {isFavoritesSphere && (
                <p className="text-xs uppercase tracking-wider text-[#D4A84B]/80 mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Tu herbario íntimo
                </p>
              )}
              <h1 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">
                {selectedPreliminary.title}
              </h1>
            </div>
          </div>

          {isFavoritesSphere ? (
            <FavoritesMasonry
              favorites={favorites}
              onDelete={handleDeleteFavorite}
              introText={selectedPreliminary.content_text || ''}
            />
          ) : hasSubPages ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="subpages-grid">
              {subPages.map((p, idx) => (
                <button
                  key={p.id}
                  onClick={() => { setSelectedPreliminarySubPage(p); window.scrollTo(0, 0); }}
                  className="group text-left bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
                  data-testid={`subpage-card-${idx}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#FBF8F1]">
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        style={{ objectPosition: p.image_position || 'center center' }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#D4A84B]/40">
                        <FileText className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start gap-2">
                      {completedLessons.includes(p.id) ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                      ) : (
                        <FileText className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                      )}
                      <p className={`font-medium group-hover:text-[#D4A84B] transition-colors ${completedLessons.includes(p.id) ? 'text-emerald-700' : 'text-gray-800'}`}>
                        {idx + 1}. {p.title}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            renderContent(currentPage || selectedPreliminary, {
              kind: selectedPreliminary.id === (currentPage || selectedPreliminary).id ? 'practice_block' : 'preliminary_block',
              id: (currentPage || selectedPreliminary).id,
              title: (currentPage || selectedPreliminary).title || '',
              parent_id: selectedPreliminary.id,
              parent_title: selectedPreliminary.title || '',
            })
          )}
        </div>
        {renderSeedTooltip()}
      </div>
    );
  }

  // Main view: Welcome + Modules gallery (Notion style)
  return (
    <div className="pt-20 min-h-screen bg-[#FDFCFA]">
      {/* Hero Section with image */}
      <div className="relative h-[300px] lg:h-[400px] overflow-hidden">
        <img 
          src={course.hero_image || 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?fit=crop&w=1200&q=80'}
          alt="Hero"
          className="w-full h-full object-cover"
          style={{ objectPosition: course.hero_image_position || 'center center' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FDFCFA] via-[#FDFCFA]/30 to-transparent" />
        
        {/* Back button */}
        <Link 
          to="/mi-camino" 
          className="absolute top-6 left-6 inline-flex items-center gap-2 text-white/80 hover:text-white bg-black/20 backdrop-blur-sm px-4 py-2 rounded-full transition-colors text-sm"
          data-testid="curso-back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Mi Camino
        </Link>
      </div>

      {/* Welcome Card */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-20 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-[hsl(var(--sage))]/10 p-8 lg:p-12 mb-12">
          <h1 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-6" data-testid="curso-title">
            {course.welcome_title || course.title || 'Bienvenida al Espacio de Formación'}
          </h1>
          
          <div className="prose prose-lg max-w-none text-gray-600">
            <SelectableText
              sourceMeta={{
                kind: 'welcome',
                id: course.id || 'welcome',
                title: course.welcome_title || course.title || 'Bienvenida',
                parent_id: course.id,
                parent_title: course.title || '',
              }}
              onRequestSave={requestSaveFavorite}
            >
              <MarkdownContent content={course.welcome_text} />
            </SelectableText>
          </div>
        </div>
      </div>

      {/* Modules Gallery - Notion style */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Overall progress bar */}
        {(() => {
          const op = getOverallProgress(blocks);
          return op.total > 0 ? (
            <div className="mb-8 bg-white rounded-xl border border-gray-100 p-5" data-testid="overall-progress">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">Tu progreso</span>
                <span className="text-sm font-semibold text-[#D4A84B]">{op.percent}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{ 
                    width: `${op.percent}%`,
                    background: op.percent === 100 
                      ? 'linear-gradient(90deg, #34d399, #10b981)' 
                      : 'linear-gradient(90deg, #D4A84B, #e8c36a)'
                  }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">{op.completed} de {op.total} lecciones completadas</p>
            </div>
          ) : null;
        })()}

        {/* Class Videos Button */}
        <button
          onClick={() => setShowClassVideos(true)}
          className="w-full mb-4 group"
          data-testid="class-videos-btn"
        >
          <div className="relative bg-gradient-to-r from-[#FBF8F1] to-[#F7F0E3] border-2 border-[#D4A84B]/30 hover:border-[#D4A84B]/60 rounded-xl px-6 py-5 flex items-center gap-5 transition-all duration-300 hover:shadow-md hover:shadow-[#D4A84B]/10">
            <div className="w-14 h-14 rounded-full bg-[#D4A84B]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A84B]/20 transition-colors">
              <Play className="w-6 h-6 text-[#D4A84B] ml-0.5" fill="#D4A84B" fillOpacity={0.2} />
            </div>
            <div className="text-left flex-1">
              <h3 className="text-lg font-heading text-[hsl(var(--text-primary))] group-hover:text-[#D4A84B] transition-colors">
                Clases en Vídeo
              </h3>
              <p className="text-sm text-gray-400 mt-0.5">
                {videoCount > 0
                  ? `${videoCount} ${videoCount === 1 ? 'clase grabada disponible' : 'clases grabadas disponibles'}`
                  : 'Accede a las grabaciones de las clases'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#D4A84B] transition-colors" />
          </div>
        </button>

        {/* Class Calendar Button - only show if calendar is enabled */}
        {calendarData.enabled && (
          <button
            onClick={() => setShowClassCalendar(true)}
            className="w-full mb-8 group"
            data-testid="class-calendar-btn"
          >
            <div className="relative bg-gradient-to-r from-[#F1F5FB] to-[#E3EDF7] border-2 border-[#7B8EBF]/30 hover:border-[#7B8EBF]/60 rounded-xl px-6 py-5 flex items-center gap-5 transition-all duration-300 hover:shadow-md hover:shadow-[#7B8EBF]/10">
              <div className="w-14 h-14 rounded-full bg-[#7B8EBF]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#7B8EBF]/20 transition-colors">
                <Calendar className="w-6 h-6 text-[#7B8EBF]" />
              </div>
              <div className="text-left flex-1">
                <h3 className="text-lg font-heading text-[hsl(var(--text-primary))] group-hover:text-[#7B8EBF] transition-colors">
                  Calendario de Clases
                </h3>
                <p className="text-sm text-gray-400 mt-0.5">
                  {calendarData.count > 0
                    ? `${calendarData.count} ${calendarData.count === 1 ? 'sesión programada' : 'sesiones programadas'}`
                    : 'Consulta las fechas de las clases en vivo'}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#7B8EBF] transition-colors" />
            </div>
          </button>
        )}

        {/* Semillas de Luz Button — Facilitadoras only (in Límites it lives as a preliminary sphere) */}
        {!isLimitesTpl && (
          <button
            onClick={() => { setShowFavoritesView(true); window.scrollTo(0, 0); }}
            className="w-full mb-8 group"
            data-testid="favorites-block-btn"
          >
            <div className="relative bg-gradient-to-r from-[#FBF5E6] to-[#F5EAD1] border-2 border-[#D4A84B]/30 hover:border-[#D4A84B]/60 rounded-xl px-6 py-5 flex items-center gap-5 transition-all duration-300 hover:shadow-md hover:shadow-[#D4A84B]/10">
              <div className="w-14 h-14 rounded-full bg-[#D4A84B]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A84B]/20 transition-colors">
                <Sprout className="w-6 h-6 text-[#D4A84B]" />
              </div>
              <div className="text-left flex-1">
                <h3 className="text-lg font-heading text-[hsl(var(--text-primary))] group-hover:text-[#D4A84B] transition-colors">
                  Semillas de Luz
                </h3>
                <p className="text-sm text-gray-400 mt-0.5">
                  {favorites.length > 0
                    ? `${favorites.length} ${favorites.length === 1 ? 'frase guardada' : 'frases guardadas'} a lo largo del curso`
                    : 'Aquí brillarán las frases que vayas subrayando en los textos'}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#D4A84B] transition-colors" />
            </div>
          </button>
        )}

        {/* Esferas introductorias (preliminary modules) — Límites only */}
        {isLimitesTpl && preliminaryBlocks.length > 0 && (
          <SpheresSection
            sectionTitle={preliminaryTitle}
            blocks={preliminaryBlocks}
            variant="preliminary"
            onSelect={(b) => { setSelectedPreliminary(b); window.scrollTo(0, 0); }}
            testId="preliminary-spheres"
            testPrefix="preliminary-sphere"
          />
        )}

        {/* Program title */}
        <div className="mb-6">
          <h2 className="text-xl font-heading text-[hsl(var(--text-primary))]">{isLimitesTpl ? 'Esferas formativas:' : 'Programa formativo:'}</h2>
        </div>

        {/* Concentric modules layout for Límites course */}
        {isLimitesTpl ? (
          <LimitesConcentricModules 
            blocks={[...blocks].filter(Boolean).sort((a, b) => (a.order || 0) - (b.order || 0))}
            onSelectModule={(block) => { setSelectedModule(block); window.scrollTo(0, 0); }}
            getModuleDisplay={getModuleDisplay}
            getModuleProgress={getModuleProgress}
            legacyLabels={courseSlug === 'limites'}
          />
        ) : (
        /* Modules grid - 3 columns, compact cards */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...blocks].filter(Boolean).sort((a, b) => (a.order || 0) - (b.order || 0)).map((block, index) => {
            const hasAccess = block.has_access;
            const moduleDisplay = getModuleDisplay(block, index);
            
            return (
              <button
                key={block.id}
                onClick={() => hasAccess && (() => { setSelectedModule(block); window.scrollTo(0, 0); })()}
                disabled={!hasAccess}
                className={`group text-left rounded-lg overflow-hidden transition-all duration-300 ${
                  hasAccess 
                    ? 'hover:shadow-lg hover:-translate-y-1 cursor-pointer' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
                data-testid={`module-${index}`}
              >
                {/* Module card - supports image or color background */}
                <div 
                  className="aspect-[4/3] relative flex flex-col justify-center items-center p-3 rounded-t-lg overflow-hidden"
                  style={{ backgroundColor: block.image_url ? (block.hide_overlay ? 'transparent' : '#000') : moduleDisplay.colors.bg }}
                >
                  {/* Background image if exists */}
                  {block.image_url && (
                    <img 
                      src={block.image_url}
                      alt={block.title}
                      className={`absolute inset-0 w-full h-full object-cover ${block.hide_overlay ? '' : 'opacity-75'}`}
                      style={{ objectPosition: block.image_position || 'center center' }}
                    />
                  )}
                  
                  {/* Overlay for better text readability on images - only if not hidden */}
                  {block.image_url && !block.hide_overlay && (
                    <div className="absolute inset-0 bg-black/20" />
                  )}
                  
                  {/* Lock icon */}
                  {!hasAccess && (
                    <div className="absolute top-2 right-2 bg-black/20 rounded-full p-1 z-10">
                      <Lock className="w-3 h-3 text-white" />
                    </div>
                  )}
                  
                  {/* Text overlay - only show if not hidden */}
                  {!block.hide_overlay && (
                    <>
                      {/* Number */}
                      <span 
                        className="text-4xl font-bold leading-none relative z-10"
                        style={{ color: block.image_url ? '#fff' : moduleDisplay.colors.text }}
                      >
                        {moduleDisplay.number}
                      </span>
                      
                      {/* Phase word */}
                      {moduleDisplay.phaseWord && (
                        <span 
                          className="text-sm font-medium mt-1 relative z-10"
                          style={{ color: block.image_url ? '#fff' : moduleDisplay.colors.text }}
                        >
                          {moduleDisplay.phaseWord}
                        </span>
                      )}
                      
                      {/* Module label */}
                      <span 
                        className="text-[10px] uppercase tracking-wide mt-0.5 opacity-80 relative z-10"
                        style={{ color: block.image_url ? '#fff' : moduleDisplay.colors.text }}
                      >
                        {moduleDisplay.number === 'i' || moduleDisplay.number === 'G' 
                          ? moduleDisplay.title 
                          : `MÓDULO ${moduleDisplay.number}`}
                      </span>
                    </>
                  )}
                </div>
                
                {/* Subtitle with heart and progress */}
                <div className="bg-white p-3 border border-gray-100 border-t-0 rounded-b-lg">
                  <div className="flex items-center gap-2">
                    <Heart className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm font-medium line-clamp-1">
                      {moduleDisplay.title || moduleDisplay.subtitle || block.subtitle || 'Sin título'}
                    </span>
                  </div>
                  {/* Mini progress bar per module */}
                  {(() => {
                    const mp = getModuleProgress(block);
                    return mp.total > 0 ? (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${mp.percent}%`,
                              backgroundColor: mp.percent === 100 ? '#10b981' : '#D4A84B'
                            }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-400 whitespace-nowrap">{mp.completed}/{mp.total}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              </button>
            );
          })}
        </div>
        )}

        {/* Esferas prácticas (practice modules) — Límites only — below the program */}
        {isLimitesTpl && practiceBlocks.length > 0 && (
          <div className="mt-12">
            <SpheresSection
              sectionTitle={practiceTitle}
              blocks={practiceBlocks}
              variant="practice"
              onSelect={(b) => { setSelectedPreliminary(b); window.scrollTo(0, 0); }}
              testId="practice-spheres"
              testPrefix="practice-sphere"
            />
          </div>
        )}

        {/* Footer info */}
        <div className="mt-12 bg-[hsl(var(--sage-light))] rounded-2xl p-6 lg:p-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="text-center lg:text-left">
              <p className="text-[hsl(var(--text-secondary))] text-sm">
                Modalidad: <span className="font-medium capitalize">{enrollment?.modality || 'preview'}</span>
              </p>
              <p className="text-[hsl(var(--text-tertiary))] text-xs mt-1">
                Los módulos se irán desbloqueando según el progreso del curso
              </p>
            </div>
            <Link to="/contacto">
              <button className="border border-[hsl(var(--sage))] text-[hsl(var(--sage))] hover:bg-[hsl(var(--sage))] hover:text-white px-4 py-2 rounded-lg transition-colors text-sm">
                ¿Necesitas ayuda?
              </button>
            </Link>
          </div>
        </div>
      </div>
      {renderSeedTooltip()}
    </div>
  );
};

export default FacilitadorasCurso;
